import React, { createContext, useCallback, useEffect, useState } from 'react';
import useAuthentication from '~/hooks/use-authentication';
import usePagination from '~/hooks/use-pagination';
import api from '~/lib/api';
import { organizationEventsFromApi } from '~/lib/data-parsers/event-flags';
import { fromApi as featureFlagsFromApi } from '~/lib/data-parsers/feature-flags';

function toApi(organization) {
  return {
    entity: organization.entity,
    hubspot_company_id: organization.hubspotCompanyId || null,
    tech_fee: organization.techFee || null,
    feature_bundle_name: organization.featureBundleName || null,
    pricelist_type_id: organization.pricelistTypeId || null,
  };
}

function fromApi(organization) {
  return {
    id: organization.id,
    uuid: organization.uuid,
    activityUrl: organization.activity_url,
    entity: organization.entity,
    hubspotCompanyId: organization.hubspot_company_id || '',
    featureBundleName: organization.feature_bundle_name || '',
    pricelistTypeId: organization.pricelist_type_id || '',
    pricelistTypeName: organization.pricelist_type_name || '',
    techFee: organization.tech_fee || '',
    featureFlags: featureFlagsFromApi(organization.feature_flags),
    eventFlags: organizationEventsFromApi(organization.event_flags),
  };
}

const OrganizationsContext = createContext();

const DEFAULT_SORTING = [{ key: 'entity', direction: 'asc' }];

function OrganizationsProvider(props) {
  const { isLoggedIn, isLoading: isAuthenticationLoading, isGod, isDemiGod } = useAuthentication();
  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const pagination = usePagination();
  const [orderBy, setOrderBy] = useState([]);

  const get = useCallback(async () => {
    if (!isLoggedIn || isAuthenticationLoading) return;
    try {
      const response = await api.get('/organizations');
      setRawData(response.data.map(fromApi));
      setLoading(false);
    } catch (e) {
      setError(e);
      setLoading(false);
    }
  }, [isLoggedIn, isAuthenticationLoading]);

  useEffect(() => {
    if (!rawData) return;
    let sortedData = [...rawData];
    const effectiveOrderBy = orderBy.length ? orderBy : DEFAULT_SORTING;
    effectiveOrderBy.forEach(({ key, direction }) => {
      sortedData = sortedData.sort((a, b) => {
        if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
        if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    });
    setData(sortedData);
  }, [orderBy, rawData]);

  async function create(organization) {
    const response = await api.post('/organizations', toApi(organization));
    setData([...data, fromApi(response.data)]);
  }

  async function update(organization) {
    const response = await api.patch(`/organizations/${organization.id}?force=true`, toApi(organization));
    setData(
      data.map(a => {
        if (a.id !== organization.id) return a;
        return fromApi(response.data);
      }),
    );
  }

  async function updateTest(organization) {
    const response = await api.patch(`/organizations/${organization.id}`, toApi(organization));
    return response;
  }

  useEffect(() => {
    get();
  }, [get, isLoggedIn, isAuthenticationLoading]);

  // organizations `data`/`rawData` is only populated with orgs the user has elevated (god-level) access to,
  // except for god/demigod, who get every organization - so it can't be used to detect them as orgGod
  // derived from rawData (not data) so it updates in the same commit as `loading`, rather than lagging
  // by an extra render behind the rawData->data sorting effect
  const isOrgGod = !isGod && !isDemiGod && !!rawData?.length;

  return (
    <OrganizationsContext.Provider
      value={{
        loading,
        error,
        data,
        isOrgGod,
        create,
        update,
        updateTest,
        pagination,
        order: {
          order: orderBy,
          orderChange: setOrderBy,
        },
      }}
      {...props}
    />
  );
}

export { OrganizationsContext, OrganizationsProvider };
